/**
 * The "Wat vertellen de vitaliteitscijfers?" banner at the top of the
 * Vitality page — a deterministic, template-driven summary built from the
 * same 7 cards' numbers the grid below already shows (plus, when available,
 * the department's participation rate and the week with the most steps).
 * Badged "AI-inzicht" the same way `vitalityScore`/`recovery` already are
 * (`metrics.ts`'s `CARD_BADGE` doc comment) — computed, not measured, not a
 * generated-by-an-LLM sentence. A real generative summary is a separate,
 * later product decision (see `vitality.metrics.ts`'s "Openstaande punten"
 * note this mirrors).
 */

import type { TFunction } from 'i18next';

import type { DashboardRange } from '@/features/statistics/api';
import type { CardData } from '@/features/vitality/deriveCards';
import { trendDirection, type CardId } from '@/features/vitality/metrics';
import { mostActiveWeekday, type DailyPoint } from '@/features/vitality/series';
import { formatNumber } from '@/lib/format';

/** The sentence-fit phrase for "({{pct}}% {{period}})" — a separate wording
 * from `statistics.range.*` (that one's a standalone dropdown option label,
 * e.g. "Laatste 7 dagen"/capitalized; this reads naturally mid-sentence,
 * e.g. "de afgelopen 7 dagen"). An explicit `{from, to}` range has no preset
 * key, so it falls back to a generic "in this period". */
export function periodPhrase(range: DashboardRange, t: TFunction): string {
  const key =
    typeof range !== 'string'
      ? 'vitality.insight.period.custom'
      : range === 'last_7_days'
        ? 'vitality.insight.period.last7Days'
        : range === 'last_30_days'
          ? 'vitality.insight.period.last30Days'
          : range === 'this_week'
            ? 'vitality.insight.period.thisWeek'
            : 'vitality.insight.period.thisMonth';
  return t(key);
}

export function buildInsightText(params: {
  cards: Record<CardId, CardData> | null;
  dailySeries: Record<CardId, DailyPoint[]> | null;
  participationPct: number | null;
  period: string;
  locale: string;
  t: TFunction;
}): string | null {
  const { cards, dailySeries, participationPct, period, locale, t } = params;
  if (!cards) return null;

  const vitality = cards.vitalityScore;
  const stress = cards.stress;
  const sleep = cards.sleep;
  if (vitality.value === null || vitality.previous === null) {
    return t('vitality.insight.unavailable');
  }

  const sentences: string[] = [];

  const vitalityDelta = Math.round(vitality.value - vitality.previous);
  const vitalityDirection = trendDirection(vitalityDelta);
  const vitalityKey =
    vitalityDirection === 'flat' ? 'flat' : vitalityDirection === 'up' ? 'up' : 'down';
  sentences.push(
    participationPct !== null
      ? t(`vitality.insight.vitality.${vitalityKey}`, {
          pct: formatNumber(vitalityDelta, { signDisplay: 'exceptZero' }),
          participation: formatNumber(Math.round(participationPct)),
          period,
        })
      : t('vitality.insight.vitality.noParticipation', {
          pct: formatNumber(vitalityDelta, { signDisplay: 'exceptZero' }),
          period,
        }),
  );

  if (stress.value !== null && stress.previous !== null) {
    const stressDelta = Math.round(stress.value - stress.previous);
    const stressDirection = trendDirection(stressDelta);
    const stressKey =
      stressDirection === 'flat' ? 'flat' : stressDirection === 'up' ? 'up' : 'down';
    const stressSentence = t(`vitality.insight.stress.${stressKey}`, {
      pct: formatNumber(Math.abs(stressDelta)),
    });

    if (sleep.value !== null && sleep.previous !== null) {
      const sleepMinutes = Math.round((sleep.value - sleep.previous) / 60);
      const sleepDirection = trendDirection(sleepMinutes);
      const sleepKey =
        sleepDirection === 'flat' ? 'same' : sleepDirection === 'up' ? 'longer' : 'shorter';
      const sleepSentence = t(`vitality.insight.sleep.${sleepKey}`, {
        minutes: Math.abs(sleepMinutes),
      });
      sentences.push(`${stressSentence} ${sleepSentence}`);
    } else {
      sentences.push(`${stressSentence}.`);
    }
  }

  const activeDay = dailySeries ? mostActiveWeekday(dailySeries.activity, locale) : null;
  if (activeDay) {
    // The weekday starts its own sentence — capitalized regardless of
    // whether `Intl.DateTimeFormat` returns it lowercase (Dutch does).
    const capitalized = activeDay.charAt(0).toUpperCase() + activeDay.slice(1);
    sentences.push(t('vitality.insight.mostActiveDay', { day: capitalized }));
  }

  return sentences.join(' ');
}
